import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Button } from "./ui/button";
import { ChevronDown, ChevronUp, BookOpen, Target, Calendar } from "lucide-react";

export default function TrainingPlanViewer({ planContent }) {
  const [expandedSections, setExpandedSections] = useState({});
  const [weeks, setWeeks] = useState([]);
  const [activeWeek, setActiveWeek] = useState(null);

  useEffect(() => {
    // Check if planContent exists
    if (!planContent) {
      setWeeks([]);
      return;
    }
    
    // Parse the plan content to extract weeks
    const weekMatches = planContent.match(/###?\s*WEEK\s+(\d+)[:\s]*(.*?)(?=###?\s*WEEK\s+\d+|##\s+BEST PRACTICES|##\s+COMPLETION|##\s+NEXT STEPS|##\s+COMPREHENSIVE RESOURCE|$)/gis);
    
    if (weekMatches && weekMatches.length > 0) {
      const parsedWeeks = weekMatches.map((match, index) => {
        const weekNumber = match.match(/WEEK\s+(\d+)/i)?.[1];
        const title = match.match(/WEEK\s+\d+[:\s]*(.*?)(?=\n|$)/i)?.[1] || "Week " + weekNumber;
        return {
          number: parseInt(weekNumber) || index + 1,
          title: title.trim(),
          content: match
        };
      });
      setWeeks(parsedWeeks);
      if (parsedWeeks.length > 0) {
        setActiveWeek(parsedWeeks[0].number);
        // Start with all weeks collapsed except first
        const initialExpanded = { [`week-${parsedWeeks[0].number}`]: true };
        setExpandedSections(initialExpanded);
      }
    }
  }, [planContent]);

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const scrollToWeek = (weekNumber) => {
    setActiveWeek(weekNumber);
    const element = document.getElementById(`week-${weekNumber}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Extract overview and other sections (with null check)
  const overview = planContent ? (planContent.match(/##\s*PROGRAM OVERVIEW(.*?)(?=##\s*MENTAL TRAINING FUNDAMENTALS|##\s*WEEKLY TRAINING|$)/is)?.[1] || '') : '';
  const fundamentals = planContent ? (planContent.match(/##\s*MENTAL TRAINING FUNDAMENTALS(.*?)(?=##\s*WEEKLY TRAINING|$)/is)?.[1] || '') : '';
  const bestPractices = planContent ? (planContent.match(/##\s*BEST PRACTICES.*?(.*?)(?=##\s*COMPLETION|##\s*NEXT STEPS|##\s*COMPREHENSIVE|$)/is)?.[1] || '') : '';
  const checklist = planContent ? (planContent.match(/##\s*COMPLETION CHECKLIST(.*?)(?=##\s*NEXT STEPS|##\s*COMPREHENSIVE|$)/is)?.[1] || '') : '';
  const nextSteps = planContent ? (planContent.match(/##\s*NEXT STEPS.*?(.*?)(?=##\s*COMPREHENSIVE|$)/is)?.[1] || '') : '';
  const resources = planContent ? (planContent.match(/##\s*COMPREHENSIVE RESOURCE LIBRARY(.*?)$/is)?.[1] || '') : '';

  // Early return if no plan content
  if (!planContent) {
    return (
      <div className="p-8 text-center text-gray-500">
        <p>No plan content available.</p>
      </div>
    );
  }

  const markdownComponents = {
    a: ({ node, ...props }) => (
      <a
        {...props}
        className="text-blue-600 hover:text-blue-800 underline font-medium"
        target="_blank"
        rel="noopener noreferrer"
      />
    ),
    h1: ({ node, ...props }) => (
      <h1 {...props} className="text-3xl font-bold mt-8 mb-4 text-gray-900 border-b-2 border-emerald-500 pb-2" />
    ),
    h2: ({ node, ...props }) => (
      <h2 {...props} className="text-2xl font-bold mt-6 mb-3 text-gray-800" />
    ),
    h3: ({ node, ...props }) => (
      <h3 {...props} className="text-xl font-semibold mt-5 mb-2 text-gray-700" />
    ),
    h4: ({ node, ...props }) => (
      <h4 {...props} className="text-lg font-semibold mt-4 mb-2 text-gray-600" />
    ),
    p: ({ node, ...props }) => (
      <p {...props} className="mb-3 text-gray-700 leading-relaxed" />
    ),
    ul: ({ node, ...props }) => (
      <ul {...props} className="list-disc pl-6 mb-4 space-y-2" />
    ),
    ol: ({ node, ...props }) => (
      <ol {...props} className="list-decimal pl-6 mb-4 space-y-2" />
    ),
    li: ({ node, ...props }) => (
      <li {...props} className="text-gray-700 leading-relaxed" />
    ),
    strong: ({ node, ...props }) => (
      <strong {...props} className="font-semibold text-gray-900" />
    ),
    code: ({ node, ...props }) => (
      <code {...props} className="bg-gray-100 px-2 py-0.5 rounded text-sm font-mono" />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote {...props} className="border-l-4 border-emerald-500 pl-4 italic text-gray-600 my-4" />
    ),
  };

  return (
    <div className="space-y-6">
      {/* Week Navigation */}
      {weeks.length > 1 && (
        <div className="bg-gradient-to-r from-emerald-50 to-sky-50 rounded-lg p-4 sticky top-0 z-10 shadow-md">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-gray-900">Program Weeks ({weeks.length} weeks total)</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {weeks.map((week) => (
              <Button
                key={week.number}
                onClick={() => scrollToWeek(week.number)}
                variant={activeWeek === week.number ? "default" : "outline"}
                size="sm"
                className={activeWeek === week.number ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              >
                Week {week.number}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Overview Section */}
      {overview && (
        <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-emerald-500">
          <button
            onClick={() => toggleSection('overview')}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <Target className="w-6 h-6 text-emerald-600" />
              <h2 className="text-2xl font-bold text-gray-900">Program Overview</h2>
            </div>
            {expandedSections['overview'] ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSections['overview'] && (
            <div className="mt-4 prose prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>{overview}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {/* Fundamentals Section */}
      {fundamentals && (
        <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-blue-500">
          <button
            onClick={() => toggleSection('fundamentals')}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Mental Training Fundamentals</h2>
            </div>
            {expandedSections['fundamentals'] ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSections['fundamentals'] && (
            <div className="mt-4 prose prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>{fundamentals}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {/* Weekly Content */}
      {weeks.map((week) => (
        <div 
          key={week.number} 
          id={`week-${week.number}`}
          className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-purple-500 scroll-mt-24"
        >
          <button
            onClick={() => toggleSection(`week-${week.number}`)}
            className="w-full flex items-center justify-between text-left"
          >
            <div>
              <span className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold mb-2">
                Week {week.number}
              </span>
              <h2 className="text-2xl font-bold text-gray-900">{week.title}</h2>
            </div>
            {expandedSections[`week-${week.number}`] ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSections[`week-${week.number}`] && (
            <div className="mt-4 prose prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>{week.content}</ReactMarkdown>
            </div>
          )}
        </div>
      ))}

      {/* Additional Sections */}
      {bestPractices && (
        <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-orange-500">
          <button
            onClick={() => toggleSection('best-practices')}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Best Practices & Success Tips</h2>
            {expandedSections['best-practices'] ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSections['best-practices'] && (
            <div className="mt-4 prose prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>{bestPractices}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {checklist && (
        <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-green-500">
          <button
            onClick={() => toggleSection('checklist')}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Completion Checklist</h2>
            {expandedSections['checklist'] ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSections['checklist'] && (
            <div className="mt-4 prose prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>{checklist}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {nextSteps && (
        <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-indigo-500">
          <button
            onClick={() => toggleSection('next-steps')}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Next Steps & Continued Growth</h2>
            {expandedSections['next-steps'] ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSections['next-steps'] && (
            <div className="mt-4 prose prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>{nextSteps}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {resources && (
        <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-pink-500">
          <button
            onClick={() => toggleSection('resources')}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Comprehensive Resource Library</h2>
            {expandedSections['resources'] ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSections['resources'] && (
            <div className="mt-4 prose prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>{resources}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {/* Fallback if no weeks detected */}
      {weeks.length === 0 && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown components={markdownComponents}>{planContent}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}
